import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import {
  Typography,
  Paper,
  Box,
  Button,
  IconButton,
  Dialog,
  DialogContent,
  Chip,
} from "@mui/material";
import { Grid, styled } from "@mui/system";
import { useNavigate, useParams } from "react-router-dom";
import ViewDocument from "../../ViewDocument/ViewDocument";
import {
  useLoaderContext,
  useToastContext,
} from "../../../../Context/ToastContext";
import styles from "./ViewRestaurant.module.css";
import { useUserContext } from "../../../../Context/UserContext";
import NearMeOutlinedIcon from "@mui/icons-material/NearMeOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { apiGet } from "../../../../utils/ApiManager";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

const fetchRestaurant = process.env.REACT_APP_FETCH_RESTAURANT_DETAILS;

//show the time of operations dayjs config
dayjs.extend(customParseFormat);

// Custom styled Paper for sections
const SectionPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(4),
  background: "linear-gradient(to right,rgb(229, 240, 233),rgb(248, 253, 248))", // Gradient background
  borderRadius: "12px",
  boxShadow: "0 4px 10px rgba(0, 0, 0, 0.2)", // Subtle shadow
  color: "#333",
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  "&:hover": {
    boxShadow: "0 6px 15px rgba(0, 0, 0, 0.3)",
  },
}));

const DetailText = styled(Typography)(({ theme }) => ({
  fontSize: "1.1rem",
  fontWeight: 500,
  marginTop: theme.spacing(1),
  color: "#555",
}));

const ViewRestaurant = () => {
  const { restaurantID } = useParams();
  const { setLoading } = useLoaderContext();
  const { showToast } = useToastContext();
  const [restaurantData, setRestaurantData] = useState({
    landlordNOC: {},
    fireNOC: {},
    policeNOC: {},
    electricityNOC: {},
  });
  const [viewDocument, setViewDocument] = useState(false);
  const [documentUrl, setDocumentUrl] = useState("");
  const [downTimeHistory, setDownTimeHistory] = useState([]);
  const [showDowntimeHistory, setShowDowntimeHistory] = useState(false);
  const [isDown, setIsDown] = useState(false);
  const [currentDownTime, setCurrentDownTime] = useState({});
  const navigateTo = useNavigate();

  const fetchRestaurantDetails = async () => {
    try {
      setLoading(true);
      const response = await apiGet(`${fetchRestaurant}/${restaurantID}`);
      if (response.success) {
        setRestaurantData(response.data);
        setDownTimeHistory(response?.downTimes || []);
        setIsDown(
          response?.downTimes?.some((data) => {
            const now = new Date();
            const start = new Date(data.startDate);
            const end = new Date(data.endDate);
            return start <= now && now <= end;
          })
        );
        setCurrentDownTime(
          response?.downTimes?.find((data) => {
            const now = new Date();
            const start = new Date(data.startDate);
            const end = new Date(data.endDate);
            return start <= now && now <= end;
          })
        );
      } else {
        showToast(
          response?.message ||
            "Failed to fetch restaurant details. Please try again.",
          "error"
        );
      }
    } catch (error) {
      showToast(
        error?.response?.message ||
          error?.message ||
          "Failed to fetch restaurant details. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleViewDocument = (documentUrl) => {
    setDocumentUrl(documentUrl);
    setViewDocument(true);
  };
  useEffect(() => {
    fetchRestaurantDetails();
  }, [restaurantID]);

  return (
    <>
      <Helmet>
        <title>Restaurant Details | DiNEST</title>
        <meta name="description" content="View restaurant details on DiNEST" />
      </Helmet>

      <Box className={styles.container}>
        <Typography variant="h4" className={styles.containerTitle}>
          Restaurant Details
        </Typography>

        {/* Restaurant Overview */}
        <SectionPaper className={styles.sectionColor}>
          <h2>Restaurant Overview</h2>
          <DetailText>
            <strong>Restaurant Name: </strong>{" "}
            {restaurantData?.restaurantName || "N/A"}
          </DetailText>
          <DetailText>
            <strong>Restaurant ID: </strong>
            {restaurantData?.restaurantId || "N/A"}
          </DetailText>
          <DetailText>
            <strong>Cuisines: </strong>
            {restaurantData?.cuisineList?.length
              ? restaurantData.cuisineList.join(", ")
              : "N/A"}
          </DetailText>
          <DetailText>
            <strong>Restaurant Number:</strong>{" "}
            {restaurantData?.restaurantNumber || "N/A"}
          </DetailText>
          <DetailText>
            <strong>Restaurant Email: </strong>
            {restaurantData?.restaurantEmail || "N/A"}
          </DetailText>
          <DetailText>
            <strong>Average Price: </strong>₹
            {restaurantData?.priceForTwo
              ? `${restaurantData.priceForTwo}/- for two`
              : "N/A"}
          </DetailText>
          {restaurantData?.location?.coordinates?.length > 0 && (
            <IconButton
              className={styles.viewDocButton}
              href={`https://www.google.com/maps?q=${restaurantData?.location?.coordinates[1]},${restaurantData?.location?.coordinates[0]}`}
              variant="outlined"
              target="_blank"
            >
              {/* View Location */}
              <NearMeOutlinedIcon color="#207d46" />
            </IconButton>
          )}
          {/* <IconButton>View on map :  </IconButton> */}
        </SectionPaper>

        {/* Contact Details */}
        <SectionPaper className={styles.sectionColor}>
          <h2>Contact Details</h2>
          <DetailText>
            <strong>Restaurant Owner: </strong>
            {restaurantData?.restaurantOwner?.firstName || "N/A"}{" "}
            {restaurantData?.restaurantOwner?.lastName || ""}
          </DetailText>
          <DetailText>
            <strong>Owner ID: </strong>
            {restaurantData?.restaurantOwner?.userId || "N/A"}
          </DetailText>
          <DetailText>
            <strong>Owner email: </strong>
            {restaurantData?.restaurantOwner?.email || "N/A"}
          </DetailText>
        </SectionPaper>

        {/* Restaurant Address */}
        <SectionPaper className={styles.sectionColor}>
          <h2>Restaurant Address</h2>
          <DetailText>
            <strong> Address Line: </strong>
            {restaurantData?.restaurantAddress?.addressLine || "N/A"}
          </DetailText>
          <DetailText>
            <strong> City: </strong>{" "}
            {restaurantData?.restaurantAddress?.city || "N/A"}
          </DetailText>
          <DetailText>
            <strong> State: </strong>{" "}
            {restaurantData?.restaurantAddress?.state || "N/A"}
          </DetailText>
          <DetailText>
            <strong> Country: </strong>{" "}
            {restaurantData?.restaurantAddress?.country || "N/A"}
          </DetailText>
          <DetailText>
            <strong> Pincode: </strong>{" "}
            {restaurantData?.restaurantAddress?.pincode || "N/A"}
          </DetailText>
        </SectionPaper>

        {/* FSSAI Details */}
        <SectionPaper className={styles.sectionColor}>
          <h2>FSSAI Details</h2>
          <DetailText>
            <strong> FSSAI License Number: </strong>{" "}
            {restaurantData?.fssaiDetails?.licenseNo || "N/A"}
          </DetailText>
          <DetailText>
            <strong> FSSAI Issued On: </strong>{" "}
            {restaurantData?.fssaiDetails?.issuedOn
              ? new Date(
                  restaurantData.fssaiDetails.issuedOn
                ).toLocaleDateString("en-GB")
              : "N/A"}
          </DetailText>
          <DetailText>
            <strong> FSSAI Valid Upto On: </strong>{" "}
            {restaurantData?.fssaiDetails?.validUpto
              ? new Date(
                  restaurantData.fssaiDetails.validUpto
                ).toLocaleDateString("en-GB")
              : "N/A"}
          </DetailText>
          {restaurantData?.fssaiDetails?.file && (
            <IconButton
              className={styles.viewDocButton}
              onClick={() =>
                handleViewDocument(restaurantData.fssaiDetails.file)
              }
              variant="outlined"
            >
              <VisibilityOutlinedIcon color="#207d46" />
            </IconButton>
          )}
        </SectionPaper>

        {/* GST Details */}
        <SectionPaper className={styles.sectionColor}>
          <h2>GST Details</h2>
          <DetailText>
            <strong> GST Number: </strong>{" "}
            {restaurantData?.gstDetails?.regNo || "N/A"}
          </DetailText>
          <DetailText>
            <strong> CGST to diner: </strong>{" "}
            {restaurantData?.gstDetails?.cgst
              ? `${restaurantData?.gstDetails?.cgst}%`
              : "N/A"}
          </DetailText>
          <DetailText>
            <strong> SGST to diner: </strong>{" "}
            {restaurantData?.gstDetails?.sgst
              ? `${restaurantData?.gstDetails?.sgst}%`
              : "N/A"}
          </DetailText>
          <DetailText>
            <strong> GST Issued On: </strong>{" "}
            {restaurantData?.gstDetails?.issuedOn
              ? new Date(restaurantData.gstDetails.issuedOn).toLocaleDateString(
                  "en-GB"
                )
              : "N/A"}
          </DetailText>

          {restaurantData?.gstDetails?.file && (
            <IconButton
              className={styles.viewDocButton}
              onClick={() => handleViewDocument(restaurantData.gstDetails.file)}
              variant="outlined"
            >
              <VisibilityOutlinedIcon color="#207d46" />
            </IconButton>
          )}
        </SectionPaper>

        {/* PAN Details */}
        <SectionPaper className={styles.sectionColor}>
          <h2>PAN Details</h2>
          <DetailText>
            <strong>PAN Number: </strong>{" "}
            {restaurantData?.panDetails?.panNo || "N/A"}
          </DetailText>

          {restaurantData?.panDetails?.file && (
            <IconButton
              className={styles.viewDocButton}
              onClick={() => handleViewDocument(restaurantData.panDetails.file)}
              variant="outlined"
            >
              <VisibilityOutlinedIcon color="#207d46" />
            </IconButton>
          )}
        </SectionPaper>

        {/* Bank Details */}
        <SectionPaper className={styles.sectionColor}>
          <h2>Bank Details</h2>
          <DetailText>
            <strong> Bank Name: </strong>{" "}
            {restaurantData?.bankDetails?.bankName || "N/A"}
          </DetailText>
          <DetailText>
            <strong> Account Number: </strong>{" "}
            {restaurantData?.bankDetails?.accountNo || "N/A"}
          </DetailText>
          <DetailText>
            <strong> IFSC Code: </strong>
            {restaurantData?.bankDetails?.ifscCode || "N/A"}
          </DetailText>
          <DetailText>
            <strong> UPI ID: </strong>{" "}
            {restaurantData?.bankDetails?.upiId || "N/A"}
          </DetailText>

          {restaurantData?.bankDetails?.file && (
            <IconButton
              className={styles.viewDocButton}
              onClick={() =>
                handleViewDocument(restaurantData.bankDetails.file)
              }
              variant="outlined"
            >
              <VisibilityOutlinedIcon color="#207d46" />
            </IconButton>
          )}
        </SectionPaper>

        {/* NOC Details */}
        <SectionPaper className={styles.sectionColor}>
          <h2>NOC Details</h2>

          <Grid container spacing={2}>
            {[
              { label: "Landlord", name: "landlordNOC" },
              { label: "Electricity Dept", name: "electricityNOC" },
              { label: "Fire Dept", name: "fireNOC" },
              { label: "Police Station", name: "policeNOC" },
            ].map(({ label, name }) => {
              const issued = restaurantData[name]?.issuedOn || null;
              const validTill = restaurantData[name]?.validTill || null;
              const comment = restaurantData[name]?.comment || "";
              const file = restaurantData[name]?.file || null;

              return (
                <Grid key={name}>
                  <Box
                    sx={{
                      backgroundColor: "#207d46",
                      borderRadius: "12px",
                      padding: 1.5,
                      height: "220px",
                      width: "220px",
                      marginTop: 2,
                      marginBottom: 2,
                      boxShadow: "0px 1px 5px rgba(0, 0, 0, 0.1)",
                      display: "flex",
                      flexDirection: "column",
                      color: "#efe4cb",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#efe4cb", fontWeight: 600, mb: 1 }}
                    >
                      {label}
                    </Typography>

                    <DetailText sx={{ color: "#efe4cb" }}>
                      <strong>Issued On:</strong>{" "}
                      {issued
                        ? new Date(issued).toLocaleDateString("en-GB")
                        : "N/A"}
                    </DetailText>

                    <DetailText sx={{ color: "#efe4cb" }}>
                      <strong>Valid Till:</strong>{" "}
                      {validTill
                        ? new Date(validTill).toLocaleDateString("en-GB")
                        : "N/A"}
                    </DetailText>

                    <DetailText sx={{ color: "#efe4cb" }}>
                      <strong>Comments:</strong> {comment || "N/A"}
                    </DetailText>

                    {file && (
                      <IconButton
                        className={styles.viewDocButton}
                        onClick={() => handleViewDocument(file)}
                        sx={{
                          color: "#efe4cb",
                          mt: "auto",
                          alignSelf: "flex-start",
                        }} // push to bottom, align to left
                      >
                        <VisibilityOutlinedIcon />
                      </IconButton>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </SectionPaper>

        {/* Eating House License */}
        <SectionPaper className={styles.sectionColor}>
          <h2>Eating House License</h2>

          {(() => {
            const base = "eatingHouseLicense";
            const issued = restaurantData[base]
              ? restaurantData[base]["issuedOn"]
              : null;
            const validTill = restaurantData[base]
              ? restaurantData[base]["validTill"]
              : null;
            const comment = restaurantData[base]
              ? restaurantData[base]["comment"]
              : "";
            const file = restaurantData[base]
              ? restaurantData[base]["file"]
              : null;

            return (
              <Box>
                <DetailText>
                  <strong>Issued On:</strong>{" "}
                  {issued
                    ? new Date(issued).toLocaleDateString("en-GB")
                    : "N/A"}
                </DetailText>

                <DetailText>
                  <strong>Valid Till:</strong>{" "}
                  {validTill
                    ? new Date(validTill).toLocaleDateString("en-GB")
                    : "N/A"}
                </DetailText>

                <DetailText>
                  <strong>Comments:</strong> {comment || "N/A"}
                </DetailText>

                {file && (
                  <IconButton
                    className={styles.viewDocButton}
                    onClick={() => handleViewDocument(file)}
                    sx={{ mt: 1 }}
                  >
                    <VisibilityOutlinedIcon />
                  </IconButton>
                )}
              </Box>
            );
          })()}
        </SectionPaper>

        {/* Rent Agreement */}
        <SectionPaper className={styles.sectionColor}>
          <h2 style={{ marginBottom: "0.5rem" }}>Rent Agreement</h2>

          {(() => {
            const base = "leaseAgreement";
            const issued = restaurantData[base]
              ? restaurantData[base]["issuedOn"]
              : null;
            const validTill = restaurantData[base]
              ? restaurantData[base]["validTill"]
              : null;
            const comment = restaurantData[base]
              ? restaurantData[base]["comment"]
              : "";
            const file = restaurantData[base]
              ? restaurantData[base]["file"]
              : null;

            return (
              <Box>
                <DetailText>
                  <strong>Issued On:</strong>{" "}
                  {issued
                    ? new Date(issued).toLocaleDateString("en-GB")
                    : "N/A"}
                </DetailText>

                <DetailText>
                  <strong>Valid Till:</strong>{" "}
                  {validTill
                    ? new Date(validTill).toLocaleDateString("en-GB")
                    : "N/A"}
                </DetailText>

                <DetailText>
                  <strong>Comments:</strong> {comment || "N/A"}
                </DetailText>

                {file && (
                  <IconButton
                    className={styles.viewDocButton}
                    onClick={() => handleViewDocument(file)}
                    sx={{ mt: 1 }}
                  >
                    <VisibilityOutlinedIcon />
                  </IconButton>
                )}
              </Box>
            );
          })()}
        </SectionPaper>

        {/* Commission Details */}
        <SectionPaper className={styles.sectionColor}>
          <h2>Payment Details</h2>
          <DetailText>
            <strong> Dinest Commission:</strong>{" "}
            {restaurantData?.commission
              ? `${restaurantData?.commission}%`
              : "N/A"}
          </DetailText>
          <DetailText>
            <strong> Advance Booking Amount:</strong>{" "}
            {restaurantData?.partialPercentage
              ? `${restaurantData?.partialPercentage}%`
              : "N/A"}
          </DetailText>
          <DetailText>
            <strong> Advance Booking Amount per person: </strong> ₹
            {restaurantData?.partialPerPerson
              ? `${restaurantData?.partialPerPerson}/-`
              : "N/A"}
          </DetailText>
          <DetailText>
            <strong> Minimum Booking Amount per person: </strong>₹
            {restaurantData?.minBookingPerPerson
              ? `${restaurantData?.minBookingPerPerson}/-`
              : "N/A"}
          </DetailText>
        </SectionPaper>

        {/* Camera Details */}
        <SectionPaper className={styles.sectionColor}>
          <h2>Camera Details</h2>
          <DetailText>
            <strong>Camera Installed: </strong>{" "}
            {restaurantData.isCameraIntalled ? "Yes" : "No"}
          </DetailText>
          <DetailText>
            <strong>Camera Details:</strong>{" "}
            {restaurantData.cameraDetails || "No details available"}
          </DetailText>
        </SectionPaper>

        {/* Restaurant Guidelines */}
        <SectionPaper className={styles.sectionColor}>
          <h2> Restaurant Guidelines</h2>
          <DetailText>
            {restaurantData.cancellationPolicyDetails ||
              "No restaurant guidelines."}
          </DetailText>
        </SectionPaper>


        {/* Additional Restaurant Details */}
        <SectionPaper className={styles.sectionColor}>
          <h2>Additional Restaurant Details</h2>
          <DetailText>
            <strong>Earliest advance booking: </strong>
            {restaurantData.bookingBeforeHours || "N/A"} hours
          </DetailText>
          <DetailText>
            <strong>Advance cancellation hours: </strong>
            {restaurantData.cancellationBeforeHours
              ? `${restaurantData.cancellationBeforeHours} hrs`
              : "N/A"}
          </DetailText>
          <DetailText>
            <strong>Operational Hours:</strong>
          </DetailText>

          {restaurantData?.serviceTimings &&
            Object.entries(restaurantData.serviceTimings).map(
              ([day, timing], index) => {
                const { openingTime, closingTime } = timing || {};

                const open = openingTime ? dayjs(openingTime, "HH:mm") : null;
                const close = closingTime ? dayjs(closingTime, "HH:mm") : null;

                return (
                  <DetailText key={index}>
                    <strong>{day}:</strong>{" "}
                    {open?.isValid() && close?.isValid()
                      ? `${open.format("hh:mm A")} - ${close.format("hh:mm A")}`
                      : "N/A"}
                  </DetailText>
                );
              }
            )}
          <DetailText>
            <strong>Max Occupancy: </strong>
            {restaurantData?.maxOccupancy
              ? `${restaurantData?.maxOccupancy} pax`
              : "N/A"}
          </DetailText>
          <DetailText>
            <strong> Amenities:</strong>{" "}
            {restaurantData?.amenities?.length > 0
              ? restaurantData.amenities.join(", ")
              : "N/A"}
          </DetailText>

          <DetailText>
            <strong>Seating Capacity: </strong>
          </DetailText>
          {restaurantData?.tables?.length > 0 ? (
            restaurantData.tables.map((table, index) => (
              <DetailText key={table._id || index}>
                <strong>
                  {table.capacity != null
                    ? `${table.capacity} Seater`
                    : "N/A Seater"}
                </strong>
                : {table.count != null ? `${table.count} Tables` : "N/A Tables"}
              </DetailText>
            ))
          ) : (
            <DetailText>N/A</DetailText>
          )}
          {isDown && (
            <DetailText>
              <strong>Restaurant Closed During : </strong>
              {isDown
                ? `${new Date(currentDownTime?.startDate).toLocaleDateString(
                    "en-GB"
                  )} to ${new Date(currentDownTime?.endDate).toLocaleDateString(
                    "en-GB"
                  )}`
                : "N/A"}
            </DetailText>
          )}

          {restaurantData?.downTime?.startDate && (
            <>
              <DetailText>
                <strong> Restaurant Closed During : </strong>{" "}
                {restaurantData?.downTime?.startDate
                  ? new Date(
                      restaurantData?.downTime?.startDate
                    ).toLocaleDateString("en-GB")
                  : "N/A"}{" "}
                to{" "}
                {restaurantData?.downTime?.endDate
                  ? new Date(
                      restaurantData?.downTime?.endDate
                    ).toLocaleDateString("en-GB")
                  : "N/A"}{" "}
              </DetailText>

              <DetailText>
                <strong>Reason for closing :</strong>{" "}
                {restaurantData?.downTime?.downtimeReason || "N/A"}{" "}
              </DetailText>
            </>
          )}

          <Button
            variant="outlined"
            sx={{ mt: 2 }}
            onClick={() => setShowDowntimeHistory(true)}
          >
            Show DownTime History
          </Button>
        </SectionPaper>

        {/* Back Button */}
        <Box sx={{ textAlign: "center" }}>
          <Button
            sx={{ backgroundColor: "#207d46" }}
            variant="contained"
            className={styles.backButton}
            onClick={() => navigateTo("/restaurants")}
          >
            Back to List
          </Button>
        </Box>
      </Box>

      <Dialog fullWidth maxWidth="xs" open={showDowntimeHistory}>
        <div
          onClick={() => setShowDowntimeHistory(false)}
          className={styles.backIconContainer}
        >
          <ArrowBackIcon className={styles.backIcon} />
        </div>

        <Typography variant="h6" className={styles.titleSettlementModal}>
          Downtime History
        </Typography>

        <DialogContent className={styles.formScrollable}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              width: "100%",
              padding: 0,
              margin: 0,
            }}
          >
            {downTimeHistory?.length > 0 ? (
              downTimeHistory.map((data, index) => {
                const isInactive = data?.isDeleted || data?.isActive === false;
                return (
                  <Box
                    key={index}
                    sx={{
                      borderRadius: 1,
                      boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
                      padding: 2,
                      color: isInactive ? "#fff" : "#000",
                      backgroundColor: isInactive ? "#f44336" : "#fafafa",
                      width: "90%",
                      overflowWrap: "break-word",
                      wordBreak: "break-word",
                    }}
                  >
                    {isInactive && (
                      <Chip
                        onClick={() => {}}
                        label={data?.isDeleted ? "Deleted" : "Inactive"}
                        size="small"
                        sx={{
                          color: "white",
                          fontSize: "0.75rem",
                          mb: 1,
                        }}
                      />
                    )}

                    <Typography>
                      <strong>Start Date:</strong>{" "}
                      {new Date(data?.startDate).toLocaleDateString("en-GB")}
                    </Typography>
                    <Typography>
                      <strong>End Date:</strong>{" "}
                      {new Date(data?.endDate).toLocaleDateString("en-GB")}
                    </Typography>
                    <Typography>
                      <strong>Reason:</strong> {data?.downtimeReason || "N/A"}
                    </Typography>
                  </Box>
                );
              })
            ) : (
              <Typography sx={{ color: "red" }}>
                No downtime history found.
              </Typography>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      <ViewDocument
        isOpen={viewDocument}
        onClose={() => setViewDocument(false)}
        documentUrl={documentUrl}
      />
    </>
  );
};

export default ViewRestaurant;
